// src/components/Header.tsx
import { useState, useEffect } from 'react';
import { Menu, X, Phone, ArrowUpRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Track scrolling for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const navLinks = [
    { name: 'Home', href: '/', id: 'home' },
    { name: 'Services', href: '/services', id: 'services' },
    { name: 'Team', href: '/team', id: 'team' },
    { name: 'Gallery', href: '/gallery', id: 'gallery' },
    { name: 'Testimonials', href: '/testimonials', id: 'testimonials' },
    { name: 'Contact', href: '/contact', id: 'contact' },
  ];

  return (
    <>
      {/* Fixed Header */}
      <header
        className={`fixed top-1 left-0 right-0 z-50 transition-all duration-300 rounded-full mx-2 ${
          isScrolled ? 'glass py-3 shadow-sm' : 'bg-transparent py-3'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo/Title */}
          <a
            href="/"
            className="flex items-center bg-barber-900/80 backdrop-blur-sm px-3 py-1 rounded-full"
          >
            <span className="text-xl font-display font-semibold tracking-tight text-white text-shadow">
              ELITE CUTS
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-medium tracking-wide bg-barber-900/80 backdrop-blur-sm px-3 py-1 rounded-full text-shadow transition-all duration-300 hover:bg-barber-900 ${
                  location.pathname === link.href
                    ? 'text-white underline underline-offset-4'
                    : 'text-gray-200 hover:text-white hover:underline hover:underline-offset-4'
                }`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Desktop CTA Button */}
          <div className="hidden md:block">
            <a
              href="/contact"
              className="bg-barber-900 text-white px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-all duration-300 hover:shadow-md hover:scale-105 active:scale-95 transform flex items-center space-x-1"
            >
              <span>Book Now</span>
              <ArrowUpRight size={16} />
            </a>
          </div>

          {/* Mobile Menu Controls */}
          <div className="flex items-center space-x-3 md:hidden">
            {/* Phone CTA */}
            <a
              href="tel:+1234567890"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-barber-900 text-white transition-all duration-300 hover:scale-105 transform"
            >
              <Phone size={20} />
              <span className="sr-only">Call Us</span>
            </a>
            {/* Hamburger Menu */}
            <button
              className="bg-barber-900/80 backdrop-blur-sm p-2 rounded-full text-white"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              <span className="sr-only">{isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Separate Mobile Menu (outside the header) */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-lg md:hidden flex flex-col"
          id="mobile-menu"
        >
          <div className="container mx-auto px-4 py-6 flex justify-between items-center">
            <a href="/" className="flex items-center">
              <span className="text-xl font-display font-semibold tracking-tight text-white text-shadow">
                ELITE CUTS
              </span>
            </a>
            <button
              className="bg-white/20 p-2 rounded-full text-white"
              onClick={toggleMobileMenu}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 overflow-y-auto">
            <div className="space-y-6 w-full max-w-md">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className={`block text-lg font-medium w-full text-center py-3 rounded-full transition-all duration-300 ${
                    location.pathname === link.href
                      ? 'bg-accent text-accent-foreground'
                      : 'text-white hover:bg-barber-900 hover:text-white'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="/contact"
                className="block w-full bg-barber-900 text-white text-center py-4 rounded-full font-medium transition-all duration-300 hover:bg-opacity-90 flex items-center justify-center space-x-1"
                onClick={toggleMobileMenu}
              >
                <span>Book Now</span>
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;